/**
 * 産業保健画面で BE が返す同意ブロック（CONSENT_REQUIRED / ANONYMOUS_MODE_RESTRICTED）の解釈。
 *
 * 課題 #26（案1で確定）: 個人詳細（HC-S02）が同意不足で閲覧できないとき、
 * 面談設定（HC-S03）も同じ理由で操作できないよう扱いを揃える。
 *
 * 以前は個人詳細だけが BE のエラーコードを解釈しており、面談設定は
 * `person.consent_granted` しか見ていなかった。そのため BE が CONSENT_REQUIRED を
 * 返した場合、面談設定では「読み込みエラー」と表示され、同意が理由だと分からなかった。
 * 同じ API（GET /v1/healthcare/person/{id}）を呼びながら扱いが違う状態だったため、
 * 判定をここに集約する。
 */
import { RequestError, type HealthcareConsentRequiredDetail } from '../api/client';

export type ConsentBlockState =
  | { kind: 'consent_required'; missing: HealthcareConsentRequiredDetail['missing_consents'] }
  | { kind: 'anonymous_restricted' };

/** RequestError.details から CONSENT_REQUIRED の構造を抽出する */
export function extractConsentDetail(details: unknown): HealthcareConsentRequiredDetail | null {
  if (!details || typeof details !== 'object') return null;
  const d = details as Record<string, unknown>;
  if (!Array.isArray(d.missing_consents)) return null;
  const missing = d.missing_consents.filter(
    (m): m is { purpose: string; data_type: string; label_ja: string } =>
      !!m &&
      typeof m === 'object' &&
      typeof (m as Record<string, unknown>).label_ja === 'string'
  );
  const consent_mode = typeof d.consent_mode === 'string' ? d.consent_mode : 'anonymous';
  return { missing_consents: missing, consent_mode };
}

/**
 * 取得エラーが同意起因なら ConsentBlockState を返す。同意起因でなければ null。
 * 呼び出し側は null のときだけ通常のエラー表示を行う。
 */
export function toConsentBlock(err: unknown): ConsentBlockState | null {
  if (!(err instanceof RequestError)) return null;
  if (err.errorCode === 'ANONYMOUS_MODE_RESTRICTED') {
    return { kind: 'anonymous_restricted' };
  }
  if (err.errorCode === 'CONSENT_REQUIRED') {
    const detail = extractConsentDetail(err.details);
    return { kind: 'consent_required', missing: detail?.missing_consents ?? [] };
  }
  return null;
}
